import React, { useEffect, useState, useRef } from "react";
import config from 'config'
import LibraryApiCall from 'LibraryApiCall';
import ButtonLoader from 'LIBRARY/CommonResources/ButtonLoader/ButtonLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactToPrint from 'react-to-print';
import ReactHtmlParser from 'react-html-parser';
import "LIBRARY/Components/Reports/ReceivedNonReceivedPeriodicalReport/ReceivedNonReceivedPeriodicalReport.css";

const formatDate = (d) => {
    const date = new Date(d);
    return date.toISOString().split('T')[0]; // yyyy-mm-dd
};

const formattedDate = (d) => {
    const date = new Date(d);
    const day = date.getDate();
    const suffix = ["th", "st", "nd", "rd"][(day % 10 > 3 || [11, 12, 13].includes(day)) ? 0 : day % 10];
    return `${day}${suffix} ${date.toLocaleString("en-US", { month: "short" })}, ${date.getFullYear()}`;
};

const getTimeValue = (value) => {
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? null : time;
};

const getPeriodicalSortValue = (item, fallbackKey) => {
    const dateCandidates = [
        item?.createdAt,
        item?.createdDate,
        item?.addedAt,
        item?.addedDate,
        item?.updatedAt,
        item?.updatedDate
    ];

    for (const candidate of dateCandidates) {
        const time = getTimeValue(candidate);
        if (time !== null) return time;
    }

    const numericId = Number(item?.periodicalIID ?? item?.periodicalId ?? item?.id ?? fallbackKey);
    return Number.isNaN(numericId) ? null : numericId;
};

/* =========================
   Component
========================= */
export default function ReceivedNonReceivedPeriodicalReport() {
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [periodicity, setPeriodicity] = useState();
    const [loading, setLoading] = useState(false);

    const [showTabs, setShowTabs] = useState(false);
    const [showValidation, setShowValidation] = useState(false);
    const [errors, setErrors] = useState({});

    const [headerData, setHeaderData] = useState({});
    const [rowData, setRowData] = useState({});

    const componentRef = useRef(null);

    useEffect(() => {
        if (!showTabs) return;

        const msg = getValidationMessage();
        if (msg) {
            setHeaderData({});
            setRowData({});
            return;
        }

        library_PeriodicalReceivedStatusReport(
            startDate,
            endDate,
            periodicity
        );
    }, [periodicity, startDate, endDate]);



    const periodicityList = [
        "Daily",
        "Weekly",
        "Fortnightly",
        "Monthly",
        "Bi Monthly",
        "Quarterly",
        "Half yearly",
        "Yearly",
        "Triennial",
        "Tri Annually",
    ];

    /* =========================
       Required Field Validation
    ========================= */
    const validateRequiredFields = () => {
        const newErrors = {};
        if (!startDate) newErrors.startDate = "Start Date is required";
        if (!endDate) newErrors.endDate = "End Date is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /* =========================
       Periodicity Validation
    ========================= */
    const getValidationMessage = () => {
        const s = new Date(startDate);
        const e = new Date(endDate);

        if (s > e) return "End Date must be greater than Start Date.";

        const diffMonths =
            (e.getFullYear() - s.getFullYear()) * 12 +
            (e.getMonth() - s.getMonth()) + 1;

        const diffYears = e.getFullYear() - s.getFullYear() + 1;

        switch (periodicity) {
            case "Bi Monthly":
                if (diffMonths < 2) return "Bi-Monthly requires at least 2 months.";
                break;
            case "Quarterly":
                if (diffMonths < 3) return "Quarterly requires at least 3 months.";
                break;
            case "Half yearly":
                if (diffMonths < 6) return "Half-Yearly requires at least 6 months.";
                break;
            case "Yearly":
                if (diffYears < 1) return "Yearly requires a full year.";
                break;
            case "Triennial":
                if (diffYears < 3) return "Triennial requires at least 3 years.";
                break;
            default:
                break;
        }
        return "";
    };

    const validationMessage = showValidation ? getValidationMessage() : "";
    const sortedHeaderData = Object.values(headerData).slice().sort((a, b) => {
        const aTime = getTimeValue(a?.startDate);
        const bTime = getTimeValue(b?.startDate);

        if (aTime !== null && bTime !== null && aTime !== bTime) return aTime - bTime;
        if (aTime !== null && bTime === null) return -1;
        if (aTime === null && bTime !== null) return 1;

        return String(a?.startDate ?? "").localeCompare(String(b?.startDate ?? ""));
    });

    const sortedRowData = Object.entries(rowData)
        .map(([key, value], index) => ({ key, value, index }))
        .sort((a, b) => {
            const aSortValue = getPeriodicalSortValue(a.value, a.key);
            const bSortValue = getPeriodicalSortValue(b.value, b.key);

            if (aSortValue !== null && bSortValue !== null && aSortValue !== bSortValue) {
                return bSortValue - aSortValue;
            }

            if (aSortValue !== null && bSortValue === null) return -1;
            if (aSortValue === null && bSortValue !== null) return 1;

            return a.index - b.index;
        })
        .map(({ value }) => value);

    const library_PeriodicalReceivedStatusReport = async (
        selectedStartDate,
        selectedEndDate,
        selectedPeriodicity
    ) => {
        let requestData = {
            iid: config.institute_id,
            startDate: formatDate(selectedStartDate),
            endDate: formatDate(selectedEndDate),
            periodicity: selectedPeriodicity
        };

        setLoading(true);

        let jsonResponse = await LibraryApiCall.makeApiCall(
            'library_PeriodicalReceivedStatusReport',
            requestData,
            '/api/library/libraryReports.php'
        );



        if (jsonResponse?.expired) {
            setTimeout(() => {
                library_PeriodicalReceivedStatusReport(
                    selectedStartDate,
                    selectedEndDate,
                    selectedPeriodicity
                );
            }, 1000);
            return;
        }

        if (jsonResponse?.success) {
            let apiResult = jsonResponse.APIResult;
            setLoading(false);
            setHeaderData(apiResult?.header);
            setRowData(apiResult?.data);
        }
    };

    /* =========================
       Actions
    ========================= */
    const loadData = () => {
        setShowValidation(true);

        if (!validateRequiredFields()) return;

        const msg = getValidationMessage();
        if (msg) {
            setHeaderData({})
            setRowData({})
            return;
        }

        const defaultPeriodicity = "Daily";

        setShowTabs(true);
        setPeriodicity(defaultPeriodicity);

        library_PeriodicalReceivedStatusReport(
            startDate,
            endDate,
            defaultPeriodicity
        );
    };


    const onTabChange = (tab) => {
        setPeriodicity(tab);
        setShowValidation(true);
    };

    const clearAll = () => {
        setStartDate("");
        setEndDate("");
        setErrors({});
        setHeaderData({})
        setRowData({})
        setShowTabs(false);
        setShowValidation(false);
        setPeriodicity("Daily");
    };

    const buildCSVHeaders = () => {
        const headers = [
            { label: "Periodical Title", key: "title" },
            { label: "ISSN Number", key: "issnNumber" },
            { label: "Periodicity", key: "periodicity" },
            { label: "Publisher", key: "publisher" },
            { label: "Amount", key: "amount" }
        ];

        sortedHeaderData.forEach(h => {
            const label =
                h.periodicity === "Daily"
                    ? formattedDate(h.startDate)
                    : `${formattedDate(h.startDate)} - ${formattedDate(h.endDate)}`;

            headers.push({
                label,
                key: label
            });
        });

        return headers;
    };

    const buildCSVData = () => {
        return sortedRowData.map(item => {
            const row = {
                title: item.title,
                issnNumber: item.issn,
                periodicity: item.periodicity,
                publisher: item.publisher,
                amount: item.amount
            };

            sortedHeaderData.forEach(h => {
                const label =
                    h.periodicity === "Daily"
                        ? formattedDate(h.startDate)
                        : `${formattedDate(h.startDate)} - ${formattedDate(h.endDate)}`;

                const match = item.copiesDetails.find(
                    d => d.startDate === h.startDate && d.endDate === h.endDate
                );

                // Choose what you want in CSV 👇
                row[label] =
                    match && match.noOfCopies > 0
                        ? `${match.noOfCopies} (₹${match.totalPrice})`
                        : "-";
            });

            return row;
        });
    };

    const exportCSV = (headers, data, fileName = "periodical_report.csv") => {
        const headerRow = headers.map(h => `"${h.label}"`).join(",");
        const rows = data.map(row =>
            headers.map(h => `"${row[h.key] ?? ""}"`).join(",")
        );

        const csvContent = [headerRow, ...rows].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        link.click();

        URL.revokeObjectURL(url);
    };

    const handleExport = () => {
        const headers = buildCSVHeaders();
        const data = buildCSVData();
        exportCSV(headers, data);
    };

    return (
        <div>
            <div className="pl-3 pr-3" style={{ height: '100%' }}>
                <div className="card" style={{
                    marginTop: '20px', boxShadow: 'none', borderRadius: '7px 7px 0px 0px', border: '1px solid #E0E8EF', padding: '12px 16px'
                }}>
                    {/* Filters + Actions */}
                    <div className="row align-items-end mb-3">

                        {/* LEFT SIDE – Filters */}
                        <div className="col-md-8 d-flex align-items-end flex-wrap" style={{ gap: '6px' }}>

                            <div>
                                <label className="form-label">
                                    Start Date <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="form-label">
                                    End Date <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                />
                            </div>

                            <button
                                className="btn btn-primary shadow-none"
                                style={{ fontSize: "14px", borderRadius: "7px" }}
                                onClick={loadData}
                            >
                                Apply
                            </button>

                            <button
                                className="btn custom-btn"
                                style={{ fontSize: "14px", borderRadius: "7px" }}
                                onClick={clearAll}
                            >
                                Clear
                            </button>
                        </div>

                        {/* RIGHT SIDE – Export & Print */}
                        {sortedRowData.length > 0 && (
                            <div className="col-md-4 d-flex justify-content-end align-items-center gap-2">
                                <div className="d-flex">
                                    {loading && <div><ButtonLoader /></div>}
                                </div>


                                {/* 1 action button - export data */}
                                <div className="d-flex ml-2" title="Click to Export" >
                                    <button className="btn outline-button" id='exportDropdownMenu' data-toggle="dropdown" aria-expanded="false" data-bs-toggle="dropdown" aria-haspopup="true" style={{ borderRadius: '5px', boxShadow: 'none' }}> <style type="text/css" media="print">
                                        {"@page { size: auto; }"}
                                    </style><FontAwesomeIcon size='lg' icon={["fa", "file-export"]} style={{ cursor: "pointer", color: '#0465AC' }} /></button>

                                    {/* 2.1 action button option - export and export all */}
                                    <div className="dropdown-menu" aria-labelledby='exportDropdownMenu'>
                                        <span>
                                            <button
                                                onClick={handleExport}
                                                className="dropdown-item"
                                                title="Click to Export"
                                                style={{ boxShadow: 'none' }}
                                            >
                                                Export
                                            </button>
                                        </span>
                                    </div>
                                </div>

                                {/* Print */}
                                <ReactToPrint
                                    trigger={() => (
                                        <button
                                            className="btn outline-button"
                                            style={{ borderRadius: '5px', boxShadow: 'none' }}
                                            title="Click to Print"
                                        >
                                            <FontAwesomeIcon
                                                icon={['fas', 'print']}
                                                size="lg"
                                                style={{ cursor: "pointer", color: '#0465AC' }}
                                            />
                                        </button>
                                    )}
                                    content={() => componentRef.current}
                                />

                            </div>
                        )}
                    </div>

                    {/* Tabs */}
                    {showTabs && (
                        <div className="mb-3 periodicity-tab-row">
                            {periodicityList.map((p) => (
                                <button
                                    key={p}
                                    type="button"
                                    className={`btn shadow-none periodicity-tab-btn ${periodicity === p ? "btn-primary" : "btn-outline-primary"}`}
                                    onClick={() => onTabChange(p)}
                                >
                                    {p}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* NOTE */}
                    {!showTabs && !loading && (
                        <p style={{ marginLeft: "20px", color: '#7A7A7A', fontSize: '14px' }}>
                            Please select <strong>Start Date</strong> and <strong>End Date</strong> to get data.
                        </p>
                    )}

                    {/* Validation Message */}
                    {validationMessage && (
                        <div className="alert alert-warning">{validationMessage}</div>
                    )}
                </div>


                {/* Table */}
                {showTabs && !validationMessage && (
                    <div style={{ overflowX: "auto", overflowY: "hidden" }}>
                        <div className="table-responsive mt-4">

                            {loading ? (
                                /* LOADER */
                                <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
                                    <ButtonLoader />
                                </div>
                            ) : (
                                <div className="print-area" ref={componentRef}>
                                    <div className="buildingReport_logo text-center mb-3">
                                        <img className="report-college-logo" alt="college-logo" src={config.instructorInfo.InstituteLogo} />
                                        <p className="report-college-name">{ReactHtmlParser(config.instructorInfo.InstituteName)}</p>
                                        <h3>Received and Non-Received Periodical Reports</h3>

                                    </div>
                                    {/* TABLE */}
                                    <div className="d-flex print-table-wrapper">

                                        {/* FIXED TITLE COLUMN */}
                                        <table className="table table-bordered vm_libraryReport_table fixed-col" style={{ width: "220px" }}>
                                            <thead>
                                                <tr>
                                                    <th>Periodical Title</th>
                                                    <th>ISSN Number</th>
                                                    <th>Periodicity</th>
                                                    <th>Publisher</th>
                                                    <th>Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sortedRowData.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td>{item.title}</td>
                                                        <td>{item.issn}</td>
                                                        <td>{item.periodicity ? item.periodicity.charAt(0).toUpperCase() + item.periodicity.slice(1) : ""}</td>
                                                        <td>{item.publisher}</td>
                                                        <td>{item.amount}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        {/* SCROLLABLE DATA */}
                                        <div className="data-table-container" style={{ overflowX: "auto", width: "100%" }}>
                                            <table className="table table-bordered vm_libraryReport_table data-col">
                                                <thead>
                                                    <tr>
                                                        {sortedHeaderData.map((h, idx) => (
                                                            <th key={idx} style={{ minWidth: "140px" }}>
                                                                {h.periodicity === "Daily"
                                                                    ? formattedDate(h.startDate)
                                                                    : `${formattedDate(h.startDate)} - ${formattedDate(h.endDate)}`}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {sortedRowData.map((item, rowIdx) => (
                                                        <tr key={rowIdx}>
                                                            {sortedHeaderData.map((h, colIdx) => {
                                                                const match = item.copiesDetails.find(
                                                                    d => d.startDate === h.startDate && d.endDate === h.endDate
                                                                );

                                                                return (
                                                                    <td key={colIdx} style={{ minWidth: "140px" }}>
                                                                        {match && match.noOfCopies > 0 ? (
                                                                            <>
                                                                                <div className="fw-bold">Copies: {match.noOfCopies}</div>
                                                                                <div className="text-success fw-semibold">
                                                                                    ₹{match.totalPrice}
                                                                                </div>
                                                                            </>
                                                                        ) : (
                                                                            <span className="text-muted">—</span>
                                                                        )}
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    ))}
                                                </tbody>

                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}

                        </div>

                    </div>
                )}
            </div>
        </div>
    );
}
